import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Userpilot } from 'userpilot'
import { MedicalBillsNew } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import AssetEmptyState from '@/matter-asset-common/asset/AssetEmptyState'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { MedSummaryAssetActions } from '../store/slice'

interface MedSummaryAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no billing summary yet. Thin wrapper over the shared
 * AssetEmptyState that supplies the billing-summary copy, icon, CTA, and the
 * first-generation-error banner (shown after a failed Generate).
 */
export default function MedSummaryAssetEmptyState(props: MedSummaryAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  // Re-run Userpilot once the empty-state lander has mounted so a feature-launch
  // announcement can match this page. The global route-change reload (routes/root.tsx)
  // fires on navigation, before this lander finishes its async asset load and paints.
  useEffect(() => {
    Userpilot.reload()
  }, [])

  return (
    <AssetEmptyState
      illustration={<MedicalBillsNew className="h-10 w-10 shrink-0 text-brand-neutral-950" />}
      title="Build your medical billing summary"
      body="Build an itemized, citation-backed summary of charges, adjustments, and payments from your medical bills — every amount linked to its source page."
      ctaLabel="Add Files"
      onCtaClick={() => navigate(matterPaths.billing.assetAddFiles(matterId))}
      notice={
        stagingTotal > 0
          ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
          : undefined
      }
      error={
        initializeError
          ? {
              headline: 'Billing summary could not be generated.',
              detail: 'Please try again.',
              dismissLabel: 'Dismiss',
              onDismiss: () => dispatch(MedSummaryAssetActions.setInitializeError({ matterId, error: null })),
            }
          : undefined
      }
    />
  )
}
